import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X } from "lucide-react";
import AiCopilot from "@/pages/modules/AiCopilot";

// The AI Audit Assistant as a help button on every page instead of an engagement panel. It opens beside the
// page it was called from and says which engagement and panel it is working on. It works on one engagement
// at a time, as its API does (/engagements/{id}/ai/...); the server applies the same engagement access as
// every other engagement route, and the roles that never see engagements (Client PIC, Component Auditor)
// do not get the button. Its suggestions stay suggestions: accepting, modifying or rejecting one records a
// decision in the audit log and changes no test status, approval or sign-off.
export const NO_ENGAGEMENT_ROLES = ["Client PIC", "Component Auditor"];

// Which assistant features fit the panel the user is on, listed first. Every feature stays available.
export const PANEL_FEATURES = {
  risk: ["risk_candidates"], "perencanaan-risiko": ["risk_candidates"], fraud: ["inquiry_consistency"],
  ewp: ["wp_review", "missing_procedures"], accounts: ["wp_review", "missing_evidence"], sampling: ["sampling_recommendation"],
  jet: ["jet_analysis"], reporting: ["fs_calk_review", "cross_reference"], evidence: ["missing_evidence"],
  lineage: ["missing_evidence", "cross_reference"], completion: ["followup_procedures", "status_summary"],
  overview: ["status_summary"], gates: ["status_summary"], substantive: ["followup_procedures"],
};

export default function AssistantButton({ context, role, openRequest = 0 }) {
  const [open, setOpen] = useState(false);
  useEffect(() => { if (openRequest) setOpen(true); }, [openRequest]);
  if (!role || NO_ENGAGEMENT_ROLES.includes(role)) return null;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="assistant-button" aria-label="Buka AI Audit Assistant"
        className="fixed bottom-4 right-4 z-30 inline-flex items-center gap-2 min-h-11 px-4 rounded-full bg-[#0B2038] text-white shadow-card-lg hover:bg-[#12304f]">
        <Sparkles size={16} /><span className="hidden sm:inline text-sm font-medium">Bantuan AI</span>
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-label="AI Audit Assistant" data-testid="assistant-panel">
          <button type="button" aria-label="Tutup AI Audit Assistant" className="flex-1 bg-black/30" onClick={() => setOpen(false)} />
          <div className="w-full sm:w-[34rem] max-w-full h-full overflow-y-auto bg-white p-4 shadow-card-lg">
            <div className="flex items-start justify-between gap-2 mb-3">
              <div className="min-w-0">
                <div className="font-head font-semibold">AI Audit Assistant</div>
                <div className="text-xs text-muted-foreground break-words" data-testid="assistant-context">
                  {context?.engagementId
                    ? <>Perikatan: <strong>{context.engagementName}</strong>{context.panelLabel ? <> · Halaman: <strong>{context.panelLabel}</strong></> : null}</>
                    : <>Halaman: <strong>{context?.pageLabel || "tanpa perikatan"}</strong></>}
                </div>
              </div>
              <button type="button" aria-label="Tutup AI Audit Assistant" onClick={() => setOpen(false)} className="min-h-11 min-w-11 flex items-center justify-center" data-testid="assistant-close"><X size={18} /></button>
            </div>
            {context?.engagementId
              ? <AiCopilot key={context.engagementId} id={context.engagementId} reload={context.reload || (() => {})} preferred={PANEL_FEATURES[context.panel] || []} />
              : <div className="text-sm space-y-2" data-testid="assistant-no-engagement">
                  <p>AI Audit Assistant bekerja pada satu perikatan: ia membaca data perikatan itu sesuai hak akses Anda dan memberi saran yang harus diputuskan auditor.</p>
                  <p>Buka perikatan dari <Link className="underline" to="/clients" onClick={() => setOpen(false)}>daftar klien</Link>, lalu tekan tombol <strong>Bantuan AI</strong> lagi.</p>
                </div>}
          </div>
        </div>
      )}
    </>
  );
}
